import React, {useRef, useState} from "react";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputTextarea} from "primereact/inputtextarea";
import {useSelector} from "react-redux";
import Avatar from "../../system/components/avatar";
import Span from "../../system/components/span";
import Icon from "../../system/components/icon";
import {useTranslate} from "../../system/useTranslate";
import {FormatToolbar, formatShortcut, formatTextBox} from "../inbox/text-formatting";

function identifier(entity) {
    return String(entity?.documentId ?? entity?.id ?? "");
}

export default function EditableComment({
    comment,
    canManageOthers = false,
    onUpdate,
    onDelete,
    onError,
    children,
}) {
    const translate = useTranslate();
    const membership = useSelector((state) => state.app?.workspaceMembershipData);
    const [editing, setEditing] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [text, setText] = useState(comment?.text || "");
    const inputRef = useRef(null);
    const isOwner = Boolean(identifier(membership) && identifier(membership) === identifier(comment?.user));
    const canModify = isOwner || canManageOthers;

    const save = async () => {
        const nextText = text.trim();
        if (!nextText || saving) return;
        setSaving(true);
        onError?.("");
        try {
            await onUpdate?.(comment, nextText);
            setEditing(false);
        } catch (error) {
            onError?.(translate("entity-comment.error.update"));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (saving) return;
        setSaving(true);
        onError?.("");
        try {
            await onDelete?.(comment);
            setDeleting(false);
        } catch (error) {
            onError?.(translate("entity-comment.error.delete"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="w-full text-xs py-2">
            <div className="flex flex-column gap-1">
                <div className="flex gap-2 align-items-center justify-content-between">
                    <div className="flex gap-2 align-items-center min-w-0">
                        <Avatar memberData={comment?.user?.member}/>
                        <div className="flex flex-column min-w-0">
                            <span className="font-bold text-2xs white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                {comment?.user?.member?.fullname}
                            </span>
                            <Span spanClassName="text-2xs text-400" type="PERSIAN_DATE" format="YYYY/MM/DD HH:mm">
                                {comment?.createdAt}
                            </Span>
                        </div>
                    </div>
                    {canModify && !editing && (
                        <div className="flex align-items-center gap-1 flex-shrink-0">
                            <Button
                                type="button"
                                text
                                rounded
                                className="w-2rem h-2rem p-0 p-button-secondary"
                                aria-label={translate("edit")}
                                tooltip={translate("edit")}
                                tooltipOptions={{position: "top"}}
                                onClick={() => {
                                    setText(comment?.text || "");
                                    setEditing(true);
                                }}
                                icon={<Icon icon="mi mi:edit"/>}
                            />
                            <Button
                                type="button"
                                text
                                rounded
                                severity="danger"
                                className="w-2rem h-2rem p-0"
                                aria-label={translate("delete")}
                                tooltip={translate("delete")}
                                tooltipOptions={{position: "top"}}
                                onClick={() => setDeleting(true)}
                                icon={<Icon icon="mi mi:delete"/>}
                            />
                        </div>
                    )}
                </div>
                <div className="pr-5 flex flex-column gap-2">
                    {editing ? (
                        <div className="p-2 w-full border-round surface-50 flex flex-column gap-2">
                            <InputTextarea
                                ref={inputRef}
                                autoFocus
                                value={text}
                                rows={3}
                                autoResize
                                className="w-full text-sm"
                                onChange={(event) => setText(event.target.value)}
                                onKeyDown={(event) => {
                                    const kind = formatShortcut(event);
                                    if (kind) {
                                        event.preventDefault();
                                        formatTextBox(inputRef.current, text, kind, setText);
                                    }
                                }}
                            />
                            <FormatToolbar onFormat={(kind) => formatTextBox(inputRef.current, text, kind, setText)}/>
                            <div className="flex align-items-center gap-2 justify-content-end">
                                <Button
                                    type="button"
                                    text
                                    className="text-xs p-2"
                                    label={translate("cancel")}
                                    onClick={() => setEditing(false)}
                                />
                                <Button
                                    type="button"
                                    className="text-xs p-2"
                                    label={translate("save")}
                                    loading={saving}
                                    disabled={!text.trim()}
                                    onClick={save}
                                />
                            </div>
                        </div>
                    ) : children}
                </div>
            </div>
            <Dialog
                dir="rtl"
                className="rtl w-25rem"
                visible={deleting}
                modal
                header={translate("entity-comment.delete.title")}
                onHide={() => setDeleting(false)}
                footer={(
                    <div className="flex align-items-center justify-content-end gap-2">
                        <Button text className="text-xs" label={translate("cancel")} onClick={() => setDeleting(false)}/>
                        <Button severity="danger" className="text-xs" label={translate("delete")} loading={saving} onClick={remove}/>
                    </div>
                )}
            >
                <p className="m-0 text-sm line-height-3">{translate("entity-comment.delete.description")}</p>
            </Dialog>
        </div>
    );
}
